import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineDelete, MdOutlineEdit, MdOutlineMoreHoriz } from 'react-icons/md';
import { Chip, TmdxAvatar, TmdxSwitch, Tooltip } from '@ds';
import { ActionMenu, ConditionalRender, MenuItemType, PartialTableColumnType, TableColumnType } from '@ui';
import { TxUserColumnKey, txUsersSortableColumnKeys } from '@features/config/dataTable/txUsers.config';
import { applySortableValues } from '@features/config/dataTable/dataTable.utils';
import { CustomerMT } from '@/api/apis/customer';
import { getFullName } from '@/utils/helpers';
import { CUSTOMER_PERMISSION_NAMES, CUSTOMER_ROLE_ADMIN_NAMES } from '@/constants/customer.constants';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const FILTERS_NAMES = {
  roles: 'Role',
  txCenters: 'TX Center Name',
};

const TX_USERS_COLUMNS: PartialTableColumnType[] = [
  col(TxUserColumnKey.contactName, 'TX User Name', {
    minWidthClassName: 'min-w-60',
  }),
  col(TxUserColumnKey.role, 'Role', {
    minWidthClassName: 'min-w-[240px]',
  }),
  col(TxUserColumnKey.txCenterName, 'TX Center Name', {
    minWidthClassName: 'min-w-[270px]',
  }),
  col(TxUserColumnKey.email, 'E-mail', {
    minWidthClassName: 'min-w-[270px]',
  }),
  col(TxUserColumnKey.primaryPhone, 'Phone', {
    minWidthClassName: 'min-w-[140px]',
  }),
  col(TxUserColumnKey.isPrimary, 'Is primary contact', {
    minWidthClassName: 'min-w-[170px]',
  }),
  col(TxUserColumnKey.isActive, 'Active', {
    minWidthClassName: 'min-w-[70px]',
  }),
  COMMON_COLUMN_PRESETS.actions,
];

export const getTXUsersTableColumns = (): TableColumnType[] => {
  return applySortableValues(TX_USERS_COLUMNS, txUsersSortableColumnKeys);
};

const getActionsMenu = (onEdit: () => void, onDelete: () => void): MenuItemType[] => [
  {
    key: 'edit-tx-user',
    type: 'action',
    to: 'edit-tx-user',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
  {
    key: 'delete-tx-user',
    type: 'action',
    to: 'delete-tx-user',
    label: (
      <div className="flex items-center gap-2 text-danger">
        <MdOutlineDelete size={16} />
        <span className="text-m-r">Delete</span>
      </div>
    ),
    onAction: () => onDelete(),
  },
];

export const getRenderTXUsersTableCell =
  (
    onEdit: (customerId: string) => void,
    onDelete: (customerId: string) => void,
    onActiveChange: (customerId: string, isActive: boolean) => void,
    hasCreatePermissions: boolean,
  ) =>
  (customer: CustomerMT, columnKey: Key): ReactNode => {
    const { id, email, customerInTxCenters, primaryPhone, photoPath, firstName, lastName, enabled, txCentersList } =
      customer;
    const onTXUserEdit = () => onEdit(id as string);
    const onTXUserDelete = () => onDelete(id as string);
    const onTXUserActiveChange = (newValue: boolean) => onActiveChange(id as string, newValue);
    const ADMIN_CAPABILITY_NAMES: Record<string, string> = {
      ...CUSTOMER_ROLE_ADMIN_NAMES,
      ...CUSTOMER_PERMISSION_NAMES,
    };

    const roles = customerInTxCenters
      ?.filter((item) => item && item.roles.length)
      .reduce((acc: string[], item) => {
        item.roles.forEach((role) => {
          const roleName = ADMIN_CAPABILITY_NAMES[role];
          if (!acc.includes(roleName)) acc.push(roleName);
        });
        return acc;
      }, [])
      .sort((a, b) => a.localeCompare(b));

    const sortedTxCentersList = txCentersList?.sort((a, b) => a.name.localeCompare(b.name));
    const isPrimary = customerInTxCenters?.map((p) => p.isPrimary);

    const ACTIONS_MENU = getActionsMenu(onTXUserEdit, onTXUserDelete);

    switch (columnKey) {
      case TxUserColumnKey.contactName:
        return (
          <Link to={`/admin/customers/tx_users/${id}`} className="flex items-center gap-2">
            <TmdxAvatar radius="xl" size="xl" name={getFullName(firstName, lastName)} src={photoPath} />
            <span className="text-m-m">{getFullName(firstName, lastName)}</span>
          </Link>
        );
      case TxUserColumnKey.role:
        return (
          <div className="flex items-center gap-2 truncate">
            <ConditionalRender show={Boolean(roles?.length)} placeholder="-">
              {roles?.map((role, index) => {
                if (!index) {
                  return (
                    <Chip key={role} size="sm">
                      {role}
                    </Chip>
                  );
                }

                if (index === 1) {
                  const slicedItems = roles.slice(index);
                  const rolesName = slicedItems.map((customerRole) => customerRole).join(', ');

                  return (
                    <Tooltip placement="top" className="max-w-64" content={rolesName} key="additionalRolesCount">
                      <Chip>{`+${roles.length - index}`}</Chip>
                    </Tooltip>
                  );
                }

                return null;
              })}
            </ConditionalRender>
          </div>
        );
      case TxUserColumnKey.txCenterName:
        return (
          <div className="flex items-center gap-2 truncate">
            <ConditionalRender show={Boolean(customerInTxCenters?.length)} placeholder="-">
              {sortedTxCentersList?.map((item, index) => {
                if (!index) {
                  return <Chip key={item.id}>{item.name}</Chip>;
                }

                if (index === 1) {
                  const slicedItems = sortedTxCentersList.slice(index);
                  const txCenterNames = slicedItems.map((txCenterName) => txCenterName.name).join(', ');

                  return (
                    <Tooltip placement="top" className="max-w-64" content={txCenterNames} key="txCentersCount">
                      <Chip>{`+${sortedTxCentersList.length - index}`}</Chip>
                    </Tooltip>
                  );
                }

                return null;
              })}
            </ConditionalRender>
          </div>
        );
      case TxUserColumnKey.email:
        return <span className="text-m-r">{email}</span>;
      case TxUserColumnKey.primaryPhone:
        return <span className="text-m-r">{primaryPhone}</span>;
      case TxUserColumnKey.isPrimary:
        return <span className="text-m-r">{isPrimary?.some((item) => item) ? 'Yes' : 'No'}</span>;
      case TxUserColumnKey.isActive:
        return (
          <TmdxSwitch
            isSelected={enabled as boolean}
            isDisabled={!hasCreatePermissions}
            onValueChange={onTXUserActiveChange}
          />
        );
      case TxUserColumnKey.actions:
        return (
          <ActionMenu
            placement="left-start"
            items={ACTIONS_MENU}
            MenuTrigger={
              <div className="">
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label={`${firstName} ${lastName} actions menu`}
          />
        );
      default:
        return null;
    }
  };
